import { useEffect, useMemo, useRef, useState } from 'react';
import { useClipboard as useMantineClipboard, useColorScheme as useMantineColorScheme, useDocumentTitle as useMantineDocumentTitle, useListState as useMantineListState, useLocalStorage as useMantineLocalStorage, } from '@mantine/hooks';
export function useDocumentTitle(title) {
    useMantineDocumentTitle(title);
}
export function useColorScheme() {
    return useMantineColorScheme();
}
export function useClipboard(config) {
    return useMantineClipboard(config);
}
export function useClickOutside(ref, handler) {
    useEffect(() => {
        const listener = (event) => {
            const node = ref.current;
            if (!node) {
                return;
            }
            if (event.target instanceof Node && !node.contains(event.target)) {
                handler();
            }
        };
        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);
        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler]);
}
export function useLocalStorage(config) {
    const { key, delay = 0 } = config;
    const [value, setValue, removeValue] = useMantineLocalStorage({
        key,
        defaultValue: null,
        getInitialValueInEffect: false,
    });
    const timeoutRef = useRef(null);
    const api = useMemo(() => ({
        save(nextValue) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (delay > 0) {
                timeoutRef.current = setTimeout(() => {
                    setValue(nextValue);
                    timeoutRef.current = null;
                }, delay);
            }
            else {
                setValue(nextValue);
            }
        },
        retrieve() {
            return value;
        },
        retrieveAndClean() {
            const currentValue = value;
            removeValue();
            return currentValue;
        },
        cancel() {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        },
    }), [delay, removeValue, setValue, value]);
    return api;
}
export function useListState(initial) {
    return useMantineListState(initial);
}
export function useIntermediateValue(config) {
    const { value, onChange, rule, format } = config;
    const [intermediateValue, setIntermediateValue] = useState(value);
    const [valid, setValid] = useState(true);
    useEffect(() => {
        setIntermediateValue(value);
    }, [value]);
    const handleChange = (nextValue) => {
        setIntermediateValue(nextValue);
        const formattedValue = format(nextValue);
        setValid(rule(formattedValue));
    };
    const handleSubmit = (nextValue) => {
        const formattedValue = format(nextValue);
        const isValid = rule(formattedValue);
        setValid(isValid);
        if (isValid) {
            onChange(formattedValue);
            setIntermediateValue(formattedValue);
        }
        else {
            setIntermediateValue(value);
        }
    };
    return {
        intermediateValue,
        valid,
        handleChange,
        handleSubmit,
    };
}
